import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getErrorMessage } from "../../api/errorMessage";
import {
  deleteServiceRequest,
  deleteServiceRequestNote,
  getServiceRequest,
  listServiceRequests,
  patchServiceRequest,
  postServiceRequestEmail,
  postServiceRequestEmailAnalysisRun,
  postServiceRequestNote,
  postServiceRequestReply,
  publicCreateServiceRequest,
  runServiceRequestAnalysis,
} from "../../api/services/serviceRequestsApi";
import { isAdminEmailMockTransportEnabled } from "../../config/adminEmailMock";
import {
  mergeEmailMessagesIntoDetail,
  patchEmailMessageClientAnalysis,
  upsertEmailMessageInList,
} from "../../utils/adminDetailEmailMessages";
import {
  buildMockInboundFollowUp,
  buildMockInboundReply,
  buildMockOutboundEmail,
} from "../../utils/adminEmailAnalysisMock";
import { pushToast } from "./toastSlice";

function snapshotRequestRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    status: row.status,
    seenAt: row.seenAt,
    unread: row.unread,
    aiAnalysis: row.aiAnalysis,
  };
}

export const fetchServiceRequestsList = createAsyncThunk(
  "serviceRequests/fetchList",
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await listServiceRequests(params);
      return { data, params };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not load service requests",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const fetchServiceRequestById = createAsyncThunk(
  "serviceRequests/fetchById",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getServiceRequest(id);
      return data;
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not load request",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const updateServiceRequest = createAsyncThunk(
  "serviceRequests/patch",
  async ({ id, body }, { dispatch, getState, rejectWithValue }) => {
    try {
      const st = getState();
      const prevRow =
        st.serviceRequests.detail?.id === id
          ? st.serviceRequests.detail
          : st.serviceRequests.list.items.find((r) => r.id === id) || null;
      const prev = snapshotRequestRow(prevRow);
      const { data } = await patchServiceRequest(id, body);
      dispatch(
        pushToast({
          variant: "success",
          title: "Saved",
          message: "Request updated.",
        })
      );
      return { id, data, prev };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Update failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const bulkPatchServiceRequests = createAsyncThunk(
  "serviceRequests/bulkPatch",
  async ({ ids, body }, { dispatch, rejectWithValue }) => {
    const ok = [];
    const fail = [];
    for (const id of ids) {
      try {
        const { data } = await patchServiceRequest(id, body);
        ok.push({ id, data });
      } catch (e) {
        fail.push({ id, error: getErrorMessage(e) });
      }
    }
    if (ok.length && !fail.length) {
      dispatch(
        pushToast({
          variant: "success",
          title: "Updated",
          message:
            ok.length === 1
              ? "1 request updated."
              : `${ok.length} requests updated.`,
        })
      );
    } else if (ok.length && fail.length) {
      dispatch(
        pushToast({
          variant: "info",
          title: "Partially updated",
          message: `${ok.length} succeeded, ${fail.length} failed.`,
        })
      );
    } else if (fail.length) {
      dispatch(
        pushToast({
          variant: "error",
          title: "Bulk update failed",
          message: fail[0]?.error || "Could not update requests.",
        })
      );
      return rejectWithValue(fail);
    }
    return { ok, fail };
  }
);

export const bulkRemoveServiceRequests = createAsyncThunk(
  "serviceRequests/bulkDelete",
  async (ids, { dispatch, rejectWithValue }) => {
    const ok = [];
    const fail = [];
    for (const id of ids) {
      try {
        await deleteServiceRequest(id);
        ok.push(id);
      } catch (e) {
        fail.push({ id, error: getErrorMessage(e) });
      }
    }
    if (ok.length && !fail.length) {
      dispatch(
        pushToast({
          variant: "success",
          title: "Deleted",
          message:
            ok.length === 1
              ? "1 request removed."
              : `${ok.length} requests removed.`,
        })
      );
    } else if (ok.length && fail.length) {
      dispatch(
        pushToast({
          variant: "info",
          title: "Partially deleted",
          message: `${ok.length} removed, ${fail.length} failed.`,
        })
      );
    } else if (fail.length) {
      dispatch(
        pushToast({
          variant: "error",
          title: "Bulk delete failed",
          message: fail[0]?.error || "Could not delete requests.",
        })
      );
      return rejectWithValue(fail);
    }
    return { ok, fail };
  }
);

export const removeServiceRequest = createAsyncThunk(
  "serviceRequests/delete",
  async (id, { dispatch, getState, rejectWithValue }) => {
    try {
      const st = getState();
      const rowEntity =
        st.serviceRequests.detail?.id === id
          ? st.serviceRequests.detail
          : st.serviceRequests.list.items.find((r) => r.id === id) || null;
      const row = snapshotRequestRow(rowEntity);
      await deleteServiceRequest(id);
      dispatch(
        pushToast({
          variant: "success",
          title: "Deleted",
          message: "Service request removed.",
        })
      );
      return { id, row };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Delete failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const APPEND_INBOUND_EMAIL = "serviceRequests/appendInboundEmailToServiceRequestDetail";

export const sendServiceRequestEmail = createAsyncThunk(
  "serviceRequests/sendEmail",
  async ({ id, subject, body }, { dispatch, getState, rejectWithValue }) => {
    const detail = getState().serviceRequests?.detail;
    const clientEmail = detail?.email ?? "";
    try {
      if (isAdminEmailMockTransportEnabled()) {
        await new Promise((r) => setTimeout(r, 480));
        const message = buildMockOutboundEmail({
          clientEmail,
          subject,
          body,
        });
        dispatch(
          pushToast({
            variant: "success",
            title: "Email sent (mock)",
            message: "Demo transport: simulated client replies will append to the thread.",
          })
        );
        const inbound = buildMockInboundReply({ clientEmail, subject, variant: "primary" });
        window.setTimeout(() => {
          dispatch({ type: APPEND_INBOUND_EMAIL, payload: { id, message: inbound } });
        }, 2000);
        const followUp = buildMockInboundFollowUp({ clientEmail, subject });
        window.setTimeout(() => {
          dispatch({ type: APPEND_INBOUND_EMAIL, payload: { id, message: followUp } });
        }, 4200);
        return { id, message };
      }

      const { data } = await postServiceRequestEmail(id, { subject, body });
      const jobId = data?.jobId ?? null;
      const message = data?.item ?? data?.message ?? null;
      const hasMessage = message && typeof message === "object" && message.id != null;
      if (hasMessage) {
        dispatch(
          pushToast({
            variant: "success",
            title: "Email sent",
            message: "Your message was delivered to the thread.",
          })
        );
      } else if (jobId) {
        dispatch(
          pushToast({
            variant: "info",
            title: "Sending…",
            message: data?.message || "Queued for delivery — the thread updates when send completes.",
          })
        );
      } else {
        dispatch(
          pushToast({
            variant: "info",
            title: "Email queued",
            message: "Your send was accepted.",
          })
        );
      }
      return { id, jobId, message: hasMessage ? message : null };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Email not sent",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const rerunServiceRequestClientEmailAnalysis = createAsyncThunk(
  "serviceRequests/rerunClientEmailAnalysis",
  async ({ id, emailMessageId }, { dispatch, rejectWithValue }) => {
    try {
      await postServiceRequestEmailAnalysisRun(id, emailMessageId, {});
      dispatch(
        pushToast({
          variant: "success",
          title: "Analysis queued",
          message: "This message will update when the model finishes.",
        })
      );
      await dispatch(fetchServiceRequestById(id));
      return { id, emailMessageId };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Re-analyse failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const sendServiceRequestReply = createAsyncThunk(
  "serviceRequests/reply",
  async ({ id, body, internal }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await postServiceRequestReply(id, {
        body,
        internal: Boolean(internal),
      });
      dispatch(
        pushToast({
          variant: "success",
          title: "Reply sent",
          message: "Message was posted.",
        })
      );
      return { id, reply: data };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Reply failed",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const addServiceRequestNote = createAsyncThunk(
  "serviceRequests/addNote",
  async ({ id, body }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await postServiceRequestNote(id, { body });
      dispatch(
        pushToast({
          variant: "success",
          title: "Note added",
        })
      );
      return { id, note: data };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not add note",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const removeServiceRequestNote = createAsyncThunk(
  "serviceRequests/deleteNote",
  async ({ id, noteId }, { dispatch, rejectWithValue }) => {
    try {
      await deleteServiceRequestNote(id, noteId);
      dispatch(
        pushToast({
          variant: "success",
          title: "Note removed",
        })
      );
      return { id, noteId };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not delete note",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const queueServiceRequestAnalysis = createAsyncThunk(
  "serviceRequests/analysisRun",
  async ({ id, instructionNote }, { dispatch, getState, rejectWithValue }) => {
    try {
      const st = getState();
      const prevRow =
        st.serviceRequests.detail?.id === id
          ? st.serviceRequests.detail
          : st.serviceRequests.list.items.find((r) => r.id === id) || null;
      const prev = snapshotRequestRow(prevRow);
      const { data } = await runServiceRequestAnalysis(
        id,
        instructionNote ? { instructionNote } : {}
      );
      dispatch(
        pushToast({
          variant: "success",
          title: "Analysis queued",
          message: "Results will appear when the worker finishes.",
        })
      );
      return { id, data, prev };
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Analysis could not start",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

export const submitPublicServiceRequest = createAsyncThunk(
  "serviceRequests/publicSubmit",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await publicCreateServiceRequest(payload);
      dispatch(
        pushToast({
          variant: "success",
          title: "Request sent",
          message: "We will get back to you shortly.",
        })
      );
      return data;
    } catch (e) {
      const msg = getErrorMessage(e);
      dispatch(
        pushToast({
          variant: "error",
          title: "Could not send request",
          message: msg,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const initialState = {
  lastListParams: {},
  list: { items: [], total: 0, page: 1, limit: 50 },
  listStatus: "idle",
  listError: null,
  detail: null,
  detailStatus: "idle",
  detailError: null,
  /** After `POST …/emails` returns `202` with `jobId` only — until `emailSent` / failure. */
  clientEmailOutbound: null,
  clientEmailSendHttpPending: false,
};

function normalizeListPayload(payload) {
  const raw = payload?.data ?? payload;
  const items = Array.isArray(raw?.items) ? raw.items : Array.isArray(raw) ? raw : [];
  const total =
    typeof raw?.total === "number" ? raw.total : items.length;
  const page = typeof raw?.page === "number" ? raw.page : 1;
  const limit = typeof raw?.limit === "number" ? raw.limit : items.length || 50;
  return { items, total, page, limit };
}

const serviceRequestsSlice = createSlice({
  name: "serviceRequests",
  initialState,
  reducers: {
    clearServiceRequestDetail(state) {
      state.detail = null;
      state.detailStatus = "idle";
      state.detailError = null;
      state.clientEmailOutbound = null;
      state.clientEmailSendHttpPending = false;
    },
    /** Merge partial row from Socket.io without full refetch */
    mergeServiceRequestFromSocket(state, action) {
      const patch = action.payload;
      if (!patch?.id) return;
      if (state.detail && state.detail.id === patch.id) {
        state.detail = { ...state.detail, ...patch };
      }
      state.list.items = state.list.items.map((row) =>
        row.id === patch.id ? { ...row, ...patch } : row
      );
    },
    applyAnalysisUpdateFromSocket(state, action) {
      const { id, aiAnalysis } = action.payload || {};
      if (!id) return;
      if (state.detail && state.detail.id === id) {
        state.detail = { ...state.detail, aiAnalysis };
      }
      state.list.items = state.list.items.map((row) =>
        row.id === id ? { ...row, aiAnalysis } : row
      );
    },
    appendInboundEmailToServiceRequestDetail(state, action) {
      const { id, message } = action.payload || {};
      if (!message || !state.detail || state.detail.id !== id) return;
      const prev = Array.isArray(state.detail.emailMessages) ? state.detail.emailMessages : [];
      state.detail = {
        ...state.detail,
        emailMessages: [...prev, message],
      };
    },
    mergeServiceRequestEmailMessage(state, action) {
      const { id, message } = action.payload || {};
      if (!message || !state.detail || String(state.detail.id) !== String(id)) return;
      const prev = state.detail.emailMessages || [];
      state.detail = {
        ...state.detail,
        emailMessages: upsertEmailMessageInList(prev, message),
      };
      const dir = String(message.direction || "").toLowerCase();
      const isOut = dir === "outbound" || dir === "out";
      if (
        isOut &&
        state.clientEmailOutbound &&
        String(state.clientEmailOutbound.parentId) === String(id)
      ) {
        state.clientEmailOutbound = null;
      }
    },
    applyServiceRequestEmailAnalysisFromSocket(state, action) {
      const { id, emailMessageId, clientAnalysis } = action.payload || {};
      if (!id || emailMessageId == null || !state.detail || String(state.detail.id) !== String(id)) {
        return;
      }
      const prev = state.detail.emailMessages || [];
      state.detail = {
        ...state.detail,
        emailMessages: patchEmailMessageClientAnalysis(prev, emailMessageId, clientAnalysis),
      };
    },
    clearServiceRequestClientEmailOutbound(state, action) {
      const { parentId, jobId } = action.payload || {};
      const cur = state.clientEmailOutbound;
      if (!cur) return;
      if (parentId != null && String(cur.parentId) !== String(parentId)) return;
      if (jobId != null && String(cur.jobId) !== String(jobId)) return;
      state.clientEmailOutbound = null;
    },
    applyServiceRequestClientEmailJobUpdated(state, action) {
      const p = action.payload || {};
      const typ = String(p.type || "").toLowerCase();
      if (typ !== "send_client_email") return;
      const status = String(p.jobStatus || "").toLowerCase();
      if (status !== "failed") return;
      const cur = state.clientEmailOutbound;
      if (!cur || String(cur.jobId) !== String(p.jobId)) return;
      if (!state.detail || String(state.detail.id) !== String(p.resourceId)) return;
      state.clientEmailOutbound = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceRequestsList.pending, (state) => {
        state.listStatus = "loading";
        state.listError = null;
      })
      .addCase(fetchServiceRequestsList.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        state.lastListParams = action.payload.params || {};
        const norm = normalizeListPayload(action.payload.data);
        state.list = norm;
        state.listError = null;
      })
      .addCase(fetchServiceRequestsList.rejected, (state, action) => {
        state.listStatus = "failed";
        state.listError = action.payload || action.error.message;
      })
      .addCase(fetchServiceRequestById.pending, (state) => {
        state.detailStatus = "loading";
        state.detailError = null;
      })
      .addCase(fetchServiceRequestById.fulfilled, (state, action) => {
        state.detailStatus = "succeeded";
        state.detail = mergeEmailMessagesIntoDetail(action.payload);
        state.detailError = null;
      })
      .addCase(fetchServiceRequestById.rejected, (state, action) => {
        state.detailStatus = "failed";
        state.detail = null;
        state.detailError = action.payload || action.error.message;
      })
      .addCase(updateServiceRequest.fulfilled, (state, action) => {
        const { id, data } = action.payload;
        if (state.detail && state.detail.id === id) {
          state.detail = { ...state.detail, ...data };
        }
        state.list.items = state.list.items.map((row) =>
          row.id === id ? { ...row, ...data } : row
        );
      })
      .addCase(removeServiceRequest.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.list.items = state.list.items.filter((r) => r.id !== id);
        state.list.total = Math.max(0, (state.list.total || 1) - 1);
        if (state.detail && state.detail.id === id) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })
      .addCase(bulkPatchServiceRequests.fulfilled, (state, action) => {
        const { ok } = action.payload || {};
        if (!Array.isArray(ok)) return;
        for (const { id, data } of ok) {
          if (state.detail && state.detail.id === id) {
            state.detail = { ...state.detail, ...data };
          }
          state.list.items = state.list.items.map((row) =>
            row.id === id ? { ...row, ...data } : row
          );
        }
      })
      .addCase(bulkRemoveServiceRequests.fulfilled, (state, action) => {
        const removed = new Set(action.payload?.ok || []);
        if (!removed.size) return;
        state.list.items = state.list.items.filter((r) => !removed.has(r.id));
        state.list.total = Math.max(0, (state.list.total || removed.size) - removed.size);
        if (state.detail && removed.has(state.detail.id)) {
          state.detail = null;
          state.detailStatus = "idle";
        }
      })
      .addCase(sendServiceRequestReply.fulfilled, (state, action) => {
        const { id, reply } = action.payload;
        if (state.detail && state.detail.id === id) {
          const replies = Array.isArray(state.detail.replies)
            ? [...state.detail.replies, reply]
            : [reply];
          state.detail = { ...state.detail, replies };
        }
      })
      .addCase(sendServiceRequestEmail.pending, (state) => {
        state.clientEmailSendHttpPending = true;
      })
      .addCase(sendServiceRequestEmail.fulfilled, (state, action) => {
        state.clientEmailSendHttpPending = false;
        const { id, jobId, message } = action.payload || {};
        if (!id || !state.detail || String(state.detail.id) !== String(id)) return;
        if (message) {
          const prev = state.detail.emailMessages || [];
          state.detail = {
            ...state.detail,
            emailMessages: upsertEmailMessageInList(prev, message),
          };
          state.clientEmailOutbound = null;
        } else if (jobId) {
          state.clientEmailOutbound = { parentId: id, jobId };
        }
      })
      .addCase(sendServiceRequestEmail.rejected, (state) => {
        state.clientEmailSendHttpPending = false;
      })
      .addCase(addServiceRequestNote.fulfilled, (state, action) => {
        const { id, note } = action.payload;
        if (state.detail && state.detail.id === id) {
          const notes = Array.isArray(state.detail.notes)
            ? [...state.detail.notes, note]
            : [note];
          state.detail = { ...state.detail, notes };
        }
      })
      .addCase(removeServiceRequestNote.fulfilled, (state, action) => {
        const { id, noteId } = action.payload;
        if (state.detail && state.detail.id === id) {
          const notes = (state.detail.notes || []).filter((n) => n.id !== noteId);
          state.detail = { ...state.detail, notes };
        }
      })
      .addCase(queueServiceRequestAnalysis.fulfilled, (state, action) => {
        const { id } = action.payload;
        if (state.detail && state.detail.id === id) {
          const aiAnalysis = {
            ...(state.detail.aiAnalysis || {}),
            status: "pending",
          };
          state.detail = { ...state.detail, aiAnalysis };
        }
        state.list.items = state.list.items.map((row) =>
          row.id === id
            ? { ...row, aiAnalysis: { ...(row.aiAnalysis || {}), status: "pending" } }
            : row
        );
      });
  },
});

export const {
  clearServiceRequestDetail,
  mergeServiceRequestFromSocket,
  applyAnalysisUpdateFromSocket,
  appendInboundEmailToServiceRequestDetail,
  mergeServiceRequestEmailMessage,
  applyServiceRequestEmailAnalysisFromSocket,
  clearServiceRequestClientEmailOutbound,
  applyServiceRequestClientEmailJobUpdated,
} = serviceRequestsSlice.actions;

export default serviceRequestsSlice.reducer;
